import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CircularProgress from "@mui/material/CircularProgress";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import CloseIcon from "@mui/icons-material/Close";

import AutoScheduleComparison from "./AutoScheduleComparison";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TuneIcon from "@mui/icons-material/Tune";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AutoScheduleEmployeeSettings from "./AutoScheduleEmployeeSettings";
import useAutoscheduleStoreDefaults from "./useAutoscheduleStoreDefaults";
import AutoScheduleOptimizationSettings, {
  openingClosingStrengthToWeight,
  roleMatchingStrengthToWeight,
} from "./AutoScheduleOptimizationSettings";

function calcLetter(score) {
  if (score >= 90) {
    return "A";
  } else if (score >= 80) {
    return "B";
  } else if (score >= 70) {
    return "C";
  } else if (score >= 60) {
    return "D";
  } else if (score === 0) {
    return "-";
  } else {
    return "F";
  }
}

const AutoScheduleFunction = React.forwardRef(function AutoScheduleFunction(props, ref) {
  const [open, setOpen] = React.useState(false);
  const [settingsOnly, setSettingsOnly] = React.useState(false);
  const [settingsTab, setSettingsTab] = React.useState("optimization");
  const [buttonPressed, setButtonPressed] = React.useState(false);
  const [secondLoadingMessage, setSecondLoadingMessage] = React.useState(false);
  const [thirdLoadingMessage, setThirdLoadingMessage] = React.useState(false);
  const [fourthLoadingMessage, setFourthLoadingMessage] = React.useState(false);
  const [fifthLoadingMessage, setFifthLoadingMessage] = React.useState(false);
  const storeDefaults = useAutoscheduleStoreDefaults(props.store_id);
  const { roleMatchingStrength, searchEffort, allowOpenShifts, openingClosingStrength, enforceOpeningClosingStaffing } = storeDefaults.draft;
  const setRoleMatchingStrength = (value) => storeDefaults.setOption("roleMatchingStrength", value);
  const setSearchEffort = (value) => storeDefaults.setOption("searchEffort", value);
  const setEnforceOpeningClosingStaffing = (value) => storeDefaults.setOption("enforceOpeningClosingStaffing", value);
  const setAllowOpenShifts = (value) => storeDefaults.setOption("allowOpenShifts", value);
  const setOpeningClosingStrength = (value) => storeDefaults.setOption("openingClosingStrength", value);

  const [scheduleComparison, setScheduleComparison] = React.useState(false);
  const [acceptedLoading, setAcceptedLoading] = React.useState(false);
  const fullWeekPastAutoscheduleEnabled =
    Boolean(props.fullWeekPastAutoscheduleEnabled);
  const canRunPastWeek = (runType) =>
    fullWeekPastAutoscheduleEnabled && runType === "autoschedule";
  const isRunUnavailable = (runType) =>
    !props.autosched_available ||
    (props.week_ended && !canRunPastWeek(runType));
  const autoscheduleUnavailable = isRunUnavailable(props.runType);
  const autoscheduleUnavailableTooltip =
    props.week_ended && !canRunPastWeek(props.runType)
      ? "Autoschedule not available for past weeks"
      : "Autoschedule not available";

  const compareOnFinish = (loading_finished) => {
    if (loading_finished) {
      setSecondLoadingMessage(false);
      setThirdLoadingMessage(false);
      setFourthLoadingMessage(false);
      setFifthLoadingMessage(false);
      setScheduleComparison(true);
    }
  };

  const handleOpen = () => {
    setSettingsOnly(false);
    setOpen(true);
  };

  React.useImperativeHandle(ref, () => ({
    openOptions: () => {
      if (autoscheduleUnavailable) return;
      setSettingsOnly(false);
      setOpen(true);
    },
    openSettings: () => {
      setSettingsOnly(true);
      setOpen(true);
    },
    runDirect: (runType) => {
      if (isRunUnavailable(runType)) return;
      setSettingsOnly(false);
      setOpen(true);
      setTimeout(() => {
        handleStart(runType);
      }, 0);
    },
  }));

  React.useEffect(() => {
    setSecondLoadingMessage(false);
    setThirdLoadingMessage(false);
    setFourthLoadingMessage(false);
    setFifthLoadingMessage(false);
    setScheduleComparison(false);
    setButtonPressed(false);
    setAcceptedLoading(false);
    props.setAutoScheduleFinished(false);
    props.setAcceptAutoScheduleFinished(false);
    props.setAutoScheduleError(false);
  }, [open])

  const handleStart = (runType) => {
    if (!storeDefaults.ready || storeDefaults.saving) return;
    props.autoSchedule(runType, {
      role_target_fit_weight: roleMatchingStrengthToWeight(
        roleMatchingStrength
      ),
      search_effort: searchEffort,
      opening_closing_consistency_weight: openingClosingStrengthToWeight(openingClosingStrength),
      allow_open_shifts: allowOpenShifts,
      enforce_opening_closing_staffing: enforceOpeningClosingStaffing,
    });
    setButtonPressed(true);
    setTimeout(() => {
      setSecondLoadingMessage(true);
    }, 5000);
    setTimeout(() => {
      setThirdLoadingMessage(true);
    }, 10000);
    setTimeout(() => {
      setFourthLoadingMessage(true);
    }, 15000);
  };

  const handleAutoFill = () => {
    props.lockAllShifts();
    props.setLockAllShifts();
    handleStart("autoschedule");
  };

  const handleClose = () => {
    setOpen(false);
    setSettingsOnly(false);
    setSecondLoadingMessage(false);
    setThirdLoadingMessage(false);
    setFourthLoadingMessage(false);
    setFifthLoadingMessage(false);
    setScheduleComparison(false);
    setButtonPressed(false);
    setAcceptedLoading(false);
    props.setAutoScheduleFinished(false);
    props.setAcceptAutoScheduleFinished(false);
    props.setAutoScheduleError(false);
  };

  function cancelAndClose() {
    handleClose();
  }
  function saveAndClose(days) {
    props.acceptNewSchedule(days);
    setAcceptedLoading(true);
  }

  useEffect(() => {
    if (props.acceptAutoScheduleFinished) {
      handleClose();
    }
  }, [props.acceptAutoScheduleFinished]);

  useEffect(() => {
    compareOnFinish(props.autoScheduleFinished);
  }, [props.autoScheduleFinished]);

  useEffect(() => {
    if (props.autoScheduleError) {
      handleClose();
    }
  }, [props.autoScheduleError]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width:
      settingsOnly
        ? "min(680px, calc(100% - 32px))"
        : buttonPressed && !props.autoScheduleFinished
        ? "30%"
        : scheduleComparison
        ? "98%"
        : "65%",
    height: settingsOnly
      ? "auto"
      : props.autoScheduleFinished
      ? "98%"
      : "content",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    paddingX: settingsOnly ? 2 : 4,
    paddingY: settingsOnly ? 2 : 3,
    maxHeight: settingsOnly ? "calc(100dvh - 32px)" : undefined,
    display: settingsOnly ? "flex" : "block",
    flexDirection: "column",
    overflow: settingsOnly ? "hidden" : "scroll",
  };

  return (
    <div>
      {props.hideTrigger ? null : autoscheduleUnavailable ?
        <div>
          <Tooltip
            title={autoscheduleUnavailableTooltip}
          >
            <div className="text-[#bdbdbd] w-10 h-10 flex items-center justify-center">
              {props.autoScheduleIcon()}
            </div>
          </Tooltip>
        </div> :
        <div>
          <Tooltip
            title={
              props.autoScheduleTitle
            }
          >
            <span>
              <IconButton
                onClick={handleOpen}
                disabled={autoscheduleUnavailable}
              >
                {props.autoScheduleIcon()}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      }
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby={
          scheduleComparison ? "schedule-comparison-title" : settingsOnly ? "autoschedule-settings-title" : "modal-modal-title"
        }
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...style, ...(scheduleComparison ? { p: 0, overflow: "hidden" } : {}) }}>
          {!buttonPressed && <div className="shrink-0 px-4 pb-3 text-sm text-slate-600">
            {!storeDefaults.ready && !storeDefaults.error && <p role="status">Loading store settings…</p>}
            {storeDefaults.error && <p role="alert">{storeDefaults.error} {!storeDefaults.ready && <button type="button" onClick={storeDefaults.reload} className="font-semibold text-violet-700 underline">Retry</button>}</p>}
            {storeDefaults.notice && <p role="status">{storeDefaults.notice}</p>}
            {settingsOnly && storeDefaults.ready && <p>Changes apply to this run. Save as store default to share them with this store's managers.</p>}
          </div>}
          {buttonPressed ? (
            scheduleComparison ? (
              <AutoScheduleComparison
                saveAndClose={saveAndClose}
                cancelAndClose={cancelAndClose}
                calcLetter={calcLetter}
                acceptedLoading={acceptedLoading}
                schedule_differences={props.schedule_differences}
                store_team_member_weekly_info={
                  props.store_team_member_weekly_info
                }
                get_t_str={props.get_t_str}
                actual_schedule={
                  props.schedule_differences?.actual_schedule ||
                  props.actual_schedule
                }
                actual_schedule_labor={
                  props.schedule_differences?.actual_schedule_labor ||
                  props.actual_schedule_labor
                }
                forecasted_labor={
                  props.schedule_differences?.forecasted_labor ||
                  props.forecasted_labor
                }
                grade_weights={props.grade_weights}
                role_types={props.role_types}
                handleClose={handleClose}
                role_names={props.role_names}
                week_start_date={props.week_start_date}
                storeNames={props.storeNames}
                store_id={props.store_id}
                comparisonMetricVisibility={props.comparisonMetricVisibility}
              />
            ) : (
              <div className="h-80">
                <div className="pb-8 border-b border-slate-200">
                  <h2 className="text-slate-900 text-xl">
                    Optimizing Your Schedule
                  </h2>
                  <p className="text-slate-500 text-sm mt-2">
                    This can take up to 5 minutes. Thank you for being patient!
                    Please don't click outside of this modal.
                  </p>
                </div>
                <div className="mt-8">
                  <div className="flex items-center gap-3">
                    {secondLoadingMessage ? (
                      <CheckCircleIcon
                        sx={{ color: "#059669", fontSize: "26px" }}
                      />
                    ) : (
                      <div className="w-6 h-6 flex items-center justify-center rounded-full border border-slate-200 bg-violet-50">
                        <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
                      </div>
                    )}
                    {secondLoadingMessage ? (
                      <p className="text-emerald-800">
                        Demand Analyzed Successfully
                      </p>
                    ) : (
                      <p>Analyzing Demand...</p>
                    )}
                  </div>
                  <div
                    className={`flex items-center gap-3 mt-5 ${
                      thirdLoadingMessage && "hidden"
                    }`}
                  >
                    {secondLoadingMessage && (
                      <div className="w-6 h-6 flex items-center justify-center rounded-full border border-slate-200 bg-violet-50">
                        <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
                      </div>
                    )}
                    {secondLoadingMessage && <p>Analyzing Availability...</p>}
                  </div>
                  <div className="flex items-center gap-3 mt-5">
                    {thirdLoadingMessage && (
                      <CheckCircleIcon
                        sx={{ color: "#059669", fontSize: "26px" }}
                      />
                    )}
                    {thirdLoadingMessage && (
                      <p className="text-emerald-800">
                        Availability Analyzed Successfully
                      </p>
                    )}
                  </div>
                  <div
                    className={`flex items-center gap-3 mt-5 ${
                      fourthLoadingMessage && "hidden"
                    }`}
                  >
                    {thirdLoadingMessage && (
                      <div className="w-6 h-6 flex items-center justify-center rounded-full border border-slate-200 bg-violet-50">
                        <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
                      </div>
                    )}
                    {thirdLoadingMessage && <p>Creating Feasible Shifts...</p>}
                  </div>
                  <div className="flex items-center gap-3 mt-5">
                    {fourthLoadingMessage && (
                      <CheckCircleIcon
                        sx={{ color: "#059669", fontSize: "26px" }}
                      />
                    )}
                    {fourthLoadingMessage && (
                      <p className="text-emerald-800">
                        Feasible Shifts Created
                      </p>
                    )}
                  </div>
                  <div
                    className={`flex items-center gap-3 mt-5 ${
                      fifthLoadingMessage && "hidden"
                    }`}
                  >
                    {fourthLoadingMessage && (
                      <div className="w-6 h-6 flex items-center justify-center rounded-full border border-slate-200 bg-violet-50">
                        <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
                      </div>
                    )}
                    {fourthLoadingMessage && <p>Finding Optimal Schedule...</p>}
                  </div>
                  <div className="flex items-center gap-3 mt-5">
                    {fifthLoadingMessage && (
                      <CheckCircleIcon
                        sx={{ color: "#059669", fontSize: "26px" }}
                      />
                    )}
                    {fifthLoadingMessage && (
                      <p className="text-emerald-800">Found Optimal Schedule</p>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : settingsOnly ? (
            <div className="flex min-h-0 w-full flex-col">
              <div className="flex shrink-0 items-start justify-between gap-6 border-b border-slate-200 px-4 pb-4 pt-2">
                <div>
                  <h2
                    id="autoschedule-settings-title"
                    className="text-lg font-semibold text-slate-900"
                  >
                    Autoschedule settings
                  </h2>
                </div>
                <IconButton
                  aria-label="Close autoschedule settings"
                  onClick={handleClose}
                  size="small"
                  sx={{ color: "#64748b" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <div role="tablist" aria-label="Autoschedule settings sections" className="flex shrink-0 gap-2 border-b border-slate-200 px-4 py-3">
                {[["optimization", "Optimization"], ["employees", "Employees"]].map(([value, label]) => (
                  <button key={value} type="button" role="tab" id={`autoschedule-${value}-tab`}
                    aria-selected={settingsTab === value} aria-controls={`autoschedule-${value}-panel`}
                    tabIndex={settingsTab === value ? 0 : -1}
                    onKeyDown={(event) => {
                      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                      event.preventDefault();
                      const next = event.key === "Home" ? "optimization" : event.key === "End" ? "employees" : value === "optimization" ? "employees" : "optimization";
                      setSettingsTab(next);
                      document.getElementById(`autoschedule-${next}-tab`)?.focus();
                    }}
                    onClick={() => setSettingsTab(value)}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${settingsTab === value ? "bg-violet-50 text-violet-700" : "text-slate-500 hover:bg-slate-50"}`}>{label}</button>
                ))}
              </div>
              <div role="tabpanel" id={`autoschedule-${settingsTab}-panel`} aria-labelledby={`autoschedule-${settingsTab}-tab`} className={`min-h-0 flex-1 px-4 py-5 ${settingsTab === "employees" ? "flex flex-col overflow-hidden" : "overflow-y-auto"}`}>
                {settingsTab === "employees" ? <AutoScheduleEmployeeSettings
                  members={props.employeeSchedulingMembers ?? props.store_team_member_weekly_info}
                  canManage={props.canManageEmployeePreferences && Boolean(props.updatePersonInfo)}
                  onSave={props.updatePersonInfo}
                /> :
                <AutoScheduleOptimizationSettings
                  roleMatchingStrength={roleMatchingStrength}
                  setRoleMatchingStrength={setRoleMatchingStrength}
                  searchEffort={searchEffort}
                  setSearchEffort={setSearchEffort}
                  openingClosingStrength={openingClosingStrength}
                  setOpeningClosingStrength={setOpeningClosingStrength}
                  allowOpenShifts={allowOpenShifts}
                  setAllowOpenShifts={setAllowOpenShifts}
                  enforceOpeningClosingStaffing={enforceOpeningClosingStaffing}
                  setEnforceOpeningClosingStaffing={setEnforceOpeningClosingStaffing}
                  compact
                />
                }
              </div>

              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 px-4 pt-4 sm:flex-col-reverse sm:items-stretch">
                {settingsTab === "optimization" ? <button
                  type="button"
                  onClick={storeDefaults.reset}
                  disabled={!storeDefaults.ready || storeDefaults.saving}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                >
                  Use store defaults
                </button> : <span className="text-xs text-slate-500">Edit an employee to update preferences.</span>}
                {settingsTab === "optimization" && props.store_id && (
                  <button type="button" onClick={storeDefaults.save}
                    disabled={!storeDefaults.ready || storeDefaults.saving}
                    className="rounded-lg border border-violet-600 px-3 py-2 text-sm font-semibold text-violet-700 disabled:opacity-50">
                    {storeDefaults.saving ? "Saving…" : "Save as store default"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="min-h-[40px] rounded-lg bg-violet-700 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <div className="w-full p-4">
              <div
                className="absolute right-5 top-5 text-slate-500 cursor-pointer"
                onClick={handleClose}
              >
                <CloseIcon />
              </div>
              <div className="max-w-2xl">
                <h2 className="text-xl font-semibold text-slate-900">
                  Autoschedule Options
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  Choose an option to use Rightwork's algorithms to help build an efficient, optimized schedule.
                  All options take into account employee availability, preferences, and target hours.
                  You will be able to review any changes made before proceeding.
                </p>
              </div>
              <div className="mt-6 bg-white rounded-xl border border-slate-200">
                <div className="p-6 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Optimization settings
                  </h3>
                  <div className="mt-5">
                    <AutoScheduleOptimizationSettings
                      roleMatchingStrength={roleMatchingStrength}
                      setRoleMatchingStrength={setRoleMatchingStrength}
                      searchEffort={searchEffort}
                      setSearchEffort={setSearchEffort}
                      openingClosingStrength={openingClosingStrength}
                      setOpeningClosingStrength={setOpeningClosingStrength}
                      allowOpenShifts={allowOpenShifts}
                      setAllowOpenShifts={setAllowOpenShifts}
                  enforceOpeningClosingStaffing={enforceOpeningClosingStaffing}
                  setEnforceOpeningClosingStaffing={setEnforceOpeningClosingStaffing}
                      compact
                    />
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
                      <TuneIcon style={{ fontSize: "28px" }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-medium text-slate-900">
                        Autotune
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Alters start and stop times of current shifts to optimize schedule
                      </p>
                    </div>

                    <button
                      disabled={!storeDefaults.ready || storeDefaults.saving}
                      onClick={() => handleStart("autotune")}
                      className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                    >
                      Go
                    </button>
                  </div>

                  <div className="pt-6 border-t border-slate-200">
                    <div className="flex items-start space-x-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
                        <AutoFixHighIcon style={{ fontSize: "28px" }} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-slate-900">
                          Autoschedule
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Builds an optimized schedule that perfectly meets demand
                        </p>
                      </div>

                      <button
                        disabled={!storeDefaults.ready || storeDefaults.saving}
                        onClick={() => handleStart("autoschedule")}
                        className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                      >
                        Go
                      </button>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-200">
                    <div className="flex items-start space-x-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
                        <PersonAddAlt1Icon style={{ fontSize: "28px" }} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-slate-900">
                          Autofill
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Creates new shifts to fill in the demand curve and prevent understaffing
                        </p>
                      </div>

                      <button
                        disabled={!storeDefaults.ready || storeDefaults.saving}
                        onClick={() => handleStart("autofill")}
                        className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                      >
                        Go
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
});

export default AutoScheduleFunction;
